import { Voucher } from '../types';
import { PdfTablePropsColumn, PdfTablePropsRow } from '../../../components/Pdfs/PdfComponents/PdfTable';
import { BillingContactPDFType } from '../../../components/Pdfs/PdfComponents/PdfBillingContactDetails';
import { taytanFormatNumberToString } from '../../../utils/mathUtils';
import { getDateByFormat } from '../../../utils/dateUtils';
import { getPdfAgentInfo } from '../../travelAgencyAccounts/helpers';
import { PdfInfoBuilderContext, BuiltPdfInfo } from './types';
import { getCurrencyDetails, getClientCurrencies, buildRateLine } from './helpers';

/**
 * Net to Remit for an Invoice, derived — never read from `info.netToRemit`.
 *
 * The stored column cannot be trusted for this type. The import writes
 * `amnt.net + amnt.airporttax` into it, but Galor's invoice lines post to income
 * accounts and carry `net = 0`, so every taxed Invoice stored the airport tax ALONE —
 * 854.00 against a 7,822.50 document, on 1,548 of them.
 *
 * `amount` is Galor's `doc.doc_total`: the gross the customer owes, carrying both the
 * tax and the commission. The commission and its VAT come off; the tax does not,
 * because it is already inside `amount`.
 *
 * This reproduces Travel Booster on both normal invoices we hold PDFs for, and it is
 * also exactly Galor's own `invnetdestamnt` on the customer line — the source system's
 * authoritative net, arrived at independently:
 *
 *   301902  23,500.00 − 1,150.00 − 195.50 = 22,154.50   TB prints 22,154.50
 *   303525  10,808.00 −   500.00 −  85.00 = 10,223.00   TB prints 10,223.00
 *
 * Known exception: 309971 is a CANCELLATION carrying a negative commission, where TB
 * prints 7,822.50 against this rule's 7,462.50. It is one of the 12 positive-document
 * cancellations carved out of the commission backfill — see the memory note
 * `service-tax-accounting-bugs`. Do not reshape this rule around that one document;
 * two independent normal invoices agree with it.
 */
function deriveNetToRemit(
    amount: number,
    commission: number,
    vatOnCommission: number,
    reduction: number
): number {
    return amount - commission - vatOnCommission - reduction;
}

/**
 * Build pdfInfo for Invoice
 * Invoice is issued TO an agent, so header shows agentInfo instead of passenger.
 * Table: Service | Dates | Description | Fare | Tax
 */
export function buildInvoicePdfInfo(
    voucher: Voucher,
    context: PdfInfoBuilderContext,
    baseInfo: BuiltPdfInfo
): BuiltPdfInfo {
    const info = voucher.info as any;
    const { code: currencyCode } = getCurrencyDetails(info?.currency, context.currencies);

    // For Invoice, use agentInfo for the "To:" header (not passenger)
    let agentInfo: BillingContactPDFType | null = null;
    if (voucher.sourceType === 'agent' && context.travelAgencyAccount) {
        agentInfo = getPdfAgentInfo(context.travelAgencyAccount);
    }

    // Fallback: if agentInfo not found from context, build from stored info
    if (!agentInfo && info?.travelAgentName) {
        agentInfo = {
            name: info.travelAgentName,
            address: '',
            city: '',
            country: '',
            postalCode: '',
            vatNumber: '',
            phones: []
        };
    }

    // Get passenger as secondary fallback
    const rawPassenger = voucher.passenger;
    const passenger = Array.isArray(rawPassenger) ? rawPassenger[0] : rawPassenger || info?.passenger;

    const amount = Number(info?.amount) || 0;
    const tax = Number(info?.tax) || 0;
    const commission = Number(info?.commission) || 0;
    const vatOnCommission = Number(info?.vatOnCommission) || 0;
    const reduction = Number(info?.reduction) || 0;

    // `amount` is the document total (Galor's `doc.doc_total`) and already carries the
    // tax, so the Fare is simply the tax taken back out. The commission is NOT removed
    // here — it comes off further down the total box, exactly as Travel Booster shows
    // it, so that the column reads as a running calculation:
    //
    //   Fare − Commission − VAT on Commission − Reduction + Tax = Net to Remit
    //
    // Checked line for line against TB's own PDFs:
    //   303525  10,808.00 − 700.00 = 10,108.00, and TB's Total row reads 10,108.00
    //           (its body being amnt per line: 10,080.00 Base Price + 28.00 reduction)
    //   301902  23,500.00 − 3,220.00 = 20,280.00, and TB's Total row reads 20,280.00
    const fare = amount - tax;

    // If per-service rows were persisted at Generate time (user's inline edits), reuse them and
    // reconstruct the Total box from the stored scalar columns (commission/vat/tax/reduction/netToRemit).
    const storedPdf = (info as any)?.pdfRows;
    if (storedPdf?.rows?.length && storedPdf?.columns?.length) {
        const netToRemit = deriveNetToRemit(amount, commission, vatOnCommission, reduction);
        const { localCurrency: lc } = getClientCurrencies(context.client, context.currencies);
        const localLabel = lc?.code || '';
        const rateToLocal = Number(info?.rate) || 1;
        const hasMeaningful = rateToLocal && rateToLocal !== 1 && localLabel && currencyCode !== localLabel;
        const rateLine = hasMeaningful && info?.printRateOfExchange !== false
            ? buildRateLine(currencyCode, rateToLocal, localLabel) : '';
        // ILS equivalent removed — not relevant for foreign suppliers
        const equivalentAmountText: string | null = null;

        const currencySuffixStored = currencyCode ? ` ${currencyCode}` : '';
        // Accounting-standard total box: deductions shown in parentheses (GAAP/IFRS).
        const pdfTotalBoxData = [
            { label: 'Total', value: `${taytanFormatNumberToString(fare)}${currencySuffixStored}` },
            ...(commission
                ? [{ label: 'Comm.-', value: `${taytanFormatNumberToString(commission)}${currencySuffixStored}` }]
                : []),
            ...(vatOnCommission
                ? [{ label: 'VAT-', value: `${taytanFormatNumberToString(vatOnCommission)}${currencySuffixStored}` }]
                : []),
            ...(reduction
                ? [{ label: 'Reduction-', value: `${taytanFormatNumberToString(reduction)}${currencySuffixStored}` }]
                : []),
            { label: 'Tax+', value: `${taytanFormatNumberToString(tax)}${currencySuffixStored}` },
            { label: 'Net to Remit', value: `${taytanFormatNumberToString(netToRemit)}${currencySuffixStored}` }
        ];

        return {
            ...baseInfo,
            columns: storedPdf.columns,
            rows: storedPdf.rows,
            summaryLine: storedPdf.summaryLine,
            agentInfo,
            passenger: agentInfo ? null : passenger,
            bottomDetails: info?.for || info?.reservationNumber || '',
            pdfTotalBoxData,
            netToRemit: taytanFormatNumberToString(netToRemit),
            netToRemitCurrency: currencyCode,
            rateLine,
            rateTableColumns: [],
            rateTableRows: [],
            equivalentAmountText
        };
    }

    // Invoice table: Service | Dates | Description | Fare | Tax
    const columns: PdfTablePropsColumn[] = [
        { title: 'Service', fieldName: 'service', style: { width: '15%' } },
        { title: 'Dates', fieldName: 'dates', style: { width: '20%' } },
        { title: 'Description', fieldName: 'description', style: { width: '35%' } },
        { title: 'Fare', fieldName: 'fare', style: { width: '15%' } },
        { title: 'Tax', fieldName: 'tax', style: { width: '15%' } }
    ];

    const startDateFormatted = getDateByFormat(info?.serviceStartDate, 'dd-MMM-yy', '');
    const endDateFormatted = getDateByFormat(info?.serviceEndDate, 'dd-MMM-yy', '');
    const datesStr = startDateFormatted && endDateFormatted
        ? `${startDateFormatted}\n${endDateFormatted}`
        : startDateFormatted || endDateFormatted || '';

    // Description: use description field from Galor (e.g., "NCE1008\nFrance\nPEGASUS ISRAEL")
    const description = info?.description || info?.for || info?.reservationNumber || '';

    const rows: PdfTablePropsRow[] = [
        {
            service: info?.serviceName || 'General',
            dates: datesStr,
            description: description,
            fare: taytanFormatNumberToString(fare),
            tax: taytanFormatNumberToString(tax)
        }
    ];

    // No inline summary line - we'll use PdfTotalBox instead
    const summaryLine = undefined;

    const netToRemit = deriveNetToRemit(amount, commission, vatOnCommission, reduction);

    const currencySuffix = currencyCode ? ` ${currencyCode}` : '';
    // Accounting-standard total box: deductions shown in parentheses (GAAP/IFRS).
    //
    // `Total` is the FARE, not `amount`. Every line below it is then a real operation
    // and the box adds up: Fare − Commission − VAT + Tax = Net to Remit. Putting the
    // tax-inclusive `amount` here left the unparenthesised `Tax` line reading as an
    // addend when it was already counted, so a reader totalling the column got
    // 25,374.50 against a stated 22,154.50. This matches Travel Booster, whose own
    // box is Total 20,280.00 · Comm.- 1,150.00 · 18% VAT- 195.50 · Tax+ 3,220.00 ·
    // Net to Remit 22,154.50 on invoice 301902. Untaxed invoices are unaffected —
    // `fare === amount` when the tax is 0.
    const pdfTotalBoxData = [
        { label: 'Total', value: `${taytanFormatNumberToString(fare)}${currencySuffix}` },
        ...(commission
            ? [{ label: 'Comm.-', value: `${taytanFormatNumberToString(commission)}${currencySuffix}` }]
            : []),
        ...(vatOnCommission
            ? [{ label: 'VAT-', value: `${taytanFormatNumberToString(vatOnCommission)}${currencySuffix}` }]
            : []),
        ...(reduction
            ? [{ label: 'Reduction-', value: `${taytanFormatNumberToString(reduction)}${currencySuffix}` }]
            : []),
        { label: 'Tax+', value: `${taytanFormatNumberToString(tax)}${currencySuffix}` },
        { label: 'Net to Remit', value: `${taytanFormatNumberToString(netToRemit)}${currencySuffix}` }
    ];

    // bottomDetails for "For:" label - use the service reference or description
    const bottomDetails = info?.for || info?.reservationNumber || '';

    // ── Rate line and equivalent (respect printLocalCurrency / printRateOfExchange from creation dialog) ──
    const { localCurrency } = getClientCurrencies(context.client, context.currencies);
    const localLabel = localCurrency?.code || '';
    const rate = context.getConvertRate(info?.currency, localCurrency);
    const rateToLocal = Number(info?.rate) || rate?.exchangeRateSell || 1;
    const hasMeaningfulRate = rateToLocal && rateToLocal !== 1 && localLabel && currencyCode !== localLabel;

    // Only show rate/equivalent when rate is meaningful and not explicitly disabled
    const rateLine = hasMeaningfulRate && info?.printRateOfExchange !== false
        ? buildRateLine(currencyCode, rateToLocal, localLabel) : '';

    // ILS equivalent removed — not relevant for foreign suppliers
    const equivalentAmountText: string | null = null;

    return {
        ...baseInfo,
        columns,
        rows,
        summaryLine,
        agentInfo,
        passenger: agentInfo ? null : passenger,
        bottomDetails,
        pdfTotalBoxData,
        netToRemit: taytanFormatNumberToString(netToRemit),
        netToRemitCurrency: currencyCode,
        rateLine,
        rateTableColumns: [],
        rateTableRows: [],
        equivalentAmountText
    };
}
